// src/Pages/ExamAttempts.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Backdrop,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SideBar from "../Component/SideBar";
import {
  fetchExamAttemptsWithFilters,
  fetchExamDetailsById,
} from "../Api/examApi";

const ExamAttempts = () => {
  const { id } = useParams();

  const [attempts, setAttempts] = useState([]);
  const [commonInfo, setCommonInfo] = useState({});
  const [attemptSearch, setAttemptSearch] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Loading state
  const [loading, setLoading] = useState(true);

  // ✅ Responsive breakpoints
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // <600px

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const exam = await fetchExamDetailsById(id);
        setCommonInfo({
          examName:
            exam.name ||
            exam.examName ||
            `${exam.generalInformation?.name || ""}`,
          subject: exam.generalInformation?.subject,
          testDate: exam.generalInformation?.testDate,
        });

        const response = await fetchExamAttemptsWithFilters(id, {
          page: 1,
          limit: 100,
        });
        setAttempts(response.data || []);
      } catch (err) {
        console.error("Failed to fetch exam attempts:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  // Filter and sort attempts by percentage (descending)
  const filteredAttempts = attempts
    .filter((item) => {
      const fullName = `${item.firstName || ""} ${item.lastName || ""}`.trim();
      return fullName.toLowerCase().includes(attemptSearch.toLowerCase());
    })
    .sort((a, b) => {
      const percentageA = a.testResult?.percentage ?? 0;
      const percentageB = b.testResult?.percentage ?? 0;
      return percentageB - percentageA;
    });

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box display="flex" flexDirection={isSmallScreen ? "column" : "row"}>
      {/* Sidebar collapses on mobile */}
      <SideBar />

      <Box flexGrow={1} p={isSmallScreen ? 1.5 : 3}>
        {/* Loading Spinner */}
        <Backdrop
          open={loading}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Title */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: isSmallScreen ? 5 : "55px" }}
        >
          <Typography variant={isSmallScreen ? "h6" : "h5"} gutterBottom>
            <b>Leaderboard</b>
          </Typography>
        </Box>

        {/* ✅ Exam Info - Always in a row */}
        <Grid container spacing={2} sx={{ mt: 2, mb: 2 }} wrap="wrap">
          <Grid item xs={12} sm={4} md={4}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Exam Name:
              </Typography>
              <Typography variant="body2">
                {commonInfo.examName || "-"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Subject:
              </Typography>
              <Typography variant="body2">
                {commonInfo.subject || "-"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Test Date:
              </Typography>
              <Typography variant="body2">
                {commonInfo.testDate
                  ? new Date(commonInfo.testDate).toLocaleDateString()
                  : "-"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Search Box */}
        <TextField
          label="Search Student"
          variant="outlined"
          size="small"
          fullWidth
          value={attemptSearch}
          onChange={(e) => setAttemptSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
            overflowX: "auto", // ✅ Horizontal scroll on mobile
          }}
        >
          <Table size={isSmallScreen ? "small" : "medium"}>
            <TableHead sx={{ backgroundColor: "#42a5f5" }}>
              <TableRow>
                {[
                  "Rank",
                  "Student",
                  "Submitted On",
                  "Marks",
                  "Attempted",
                  "Correct",
                  "Incorrect",
                  "Percentage",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap", // ✅ Prevent wrapping
                      fontSize: isSmallScreen ? "0.75rem" : "0.9rem",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredAttempts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No users have taken this exam yet.
                  </TableCell>
                </TableRow>
              ) : (
                filteredAttempts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => {
                    const marks = item.testResult?.marks ?? 0;
                    const attempted = item.testResult?.attempted ?? 0;
                    const correct = item.testResult?.correct ?? 0;
                    const incorrect = item.testResult?.incorrect ?? 0;
                    const percentage = item.testResult?.percentage ?? 0;

                    return (
                      <TableRow key={index}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          {item.firstName || item.lastName
                            ? `${item.firstName || ""} ${
                                item.lastName || ""
                              }`.trim()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {item.submittedAt
                            ? new Date(item.submittedAt).toLocaleString()
                            : "-"}
                        </TableCell>
                        <TableCell>{marks}</TableCell>
                        <TableCell>{attempted}</TableCell>
                        <TableCell>{correct}</TableCell>
                        <TableCell>{incorrect}</TableCell>
                        <TableCell>{percentage}%</TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredAttempts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isSmallScreen ? "Rows" : "Rows per page"}
            sx={{
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: isSmallScreen ? "0.75rem" : "0.9rem",
                },
            }}
          />
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ExamAttempts;
